/**
 * JS-1081: pure exit-rule -> POSITION_EXIT friendly-field mapper.
 *
 * Lives in `domains/watches/lib` (not `domains/trade-ideas/lib`, where this
 * logic originally lived) per the adversarial-review FIX 1 correction: the
 * Watches wizard (`domains/watches/components/WatchCreateWizard`) must
 * recompute these friendly fields LIVE, from the wizard's own CURRENT draft
 * basis/quantity, every time either changes -- never just once at hydration
 * time. Doing that recompute from inside the wizard component would require
 * `domains/watches` to import from `domains/trade-ideas`, which is the WRONG
 * direction (this frontend mirrors the backend's own dependency rule: only
 * `trade_ideas` depends on `watches`, never the reverse -- see
 * `services/apis/domains/trade_ideas/exit_rule_watch_mapping.py`'s module
 * docstring for the verified backend precedent). Moving the pure arithmetic
 * itself into `domains/watches/lib` resolves the direction cleanly: the
 * wizard (inside `domains/watches`) can call it directly, and
 * `domains/trade-ideas/lib/exitRuleWatchMapping.ts` re-exports it for the
 * Desk-idea "+Watch" pre-fill's own (correctly one-directional) use.
 *
 * The function itself, its arithmetic, and its test coverage are otherwise
 * UNCHANGED from the original `domains/trade-ideas/lib/exitRuleWatchMapping.ts`
 * module -- only the location moved. See that module's history for the
 * original numeric-parity verification against the Python mapper
 * (`services/apis/domains/trade_ideas/exit_rule_watch_mapping.py`).
 */

// A structurally generic shape -- deliberately NOT importing
// `MultiStrategyExitRule` from `domains/trade-ideas/lib/contracts` (that
// would recreate the exact reverse-dependency problem this move exists to
// avoid). Any caller with these four fields, regardless of domain, can use
// this mapper.
export type ExitRuleLike = {
  rule_kind?: string | null;
  target_value?: string | null;
  stop_value?: string | null;
  dte_floor?: number | null;
};

export type ExitRuleWatchFields = {
  profitTarget?: string;
  lossLimit?: string;
  maxProfitPercent?: string;
  maxLossPercent?: string;
  dteThreshold?: string;
  dteOperator?: "lte";
};

// ---------------------------------------------------------------------------
// Exact fixed-point decimal helpers (BigInt-backed, no floating point).
// ---------------------------------------------------------------------------

/** Parse a canonical (optionally signed) decimal string into {sign, digits, scale}. */
function parseDecimal(value: string): { negative: boolean; unscaled: bigint; scale: number } {
  const trimmed = value.trim();
  const negative = trimmed.startsWith("-");
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const [whole, fraction = ""] = unsigned.split(".");
  const scale = fraction.length;
  const unscaled = BigInt((whole || "0") + fraction || "0");
  return { negative, unscaled, scale };
}

/** Render a signed, scaled BigInt back to a canonical decimal string. */
function renderDecimal(unscaled: bigint, scale: number): string {
  const negative = unscaled < 0n;
  let digits = (negative ? -unscaled : unscaled).toString();
  if (scale === 0) return `${negative && unscaled !== 0n ? "-" : ""}${digits}`;
  digits = digits.padStart(scale + 1, "0");
  const whole = digits.slice(0, digits.length - scale);
  const fraction = digits.slice(digits.length - scale).replace(/0+$/, "");
  const rendered = fraction ? `${whole}.${fraction}` : whole;
  return negative && unscaled !== 0n ? `-${rendered}` : rendered;
}

/** Multiply two exact decimal strings, returning an exact decimal string. */
function decimalMultiply(a: string, b: string): string {
  const left = parseDecimal(a);
  const right = parseDecimal(b);
  const product = left.unscaled * right.unscaled;
  const negative = left.negative !== right.negative;
  return renderDecimal(negative ? -product : product, left.scale + right.scale);
}

/** Subtract exact decimal string `b` from exact decimal string `a`. */
function decimalSubtract(a: string, b: string): string {
  const left = parseDecimal(a);
  const right = parseDecimal(b);
  const scale = Math.max(left.scale, right.scale);
  const leftScaled = (left.negative ? -1n : 1n) * left.unscaled * 10n ** BigInt(scale - left.scale);
  const rightScaled = (right.negative ? -1n : 1n) * right.unscaled * 10n ** BigInt(scale - right.scale);
  return renderDecimal(leftScaled - rightScaled, scale);
}

/** Negate an exact decimal string. */
function decimalNegate(value: string): string {
  if (value === "0" || /^0(\.0+)?$/.test(value)) return value;
  return value.startsWith("-") ? value.slice(1) : `-${value}`;
}

// One contract == 100 shares, matching the wizard's own default sizing
// (`MultiLegDraftLeg.contracts` defaults to "1") -- never a different
// default, mirroring the Python mapper's `_CONTRACT_MULTIPLIER`.
const CONTRACT_MULTIPLIER = "100";

/**
 * `(multiple - 1) * fillPricePerShare * 100 * contracts`, matching the
 * Python mapper's `_value_multiple_pnl_usd`.
 */
function valueMultiplePnlUsd(multiple: string, fillPricePerShare: string, contracts: string): string {
  const perShareDelta = decimalSubtract(multiple, "1");
  return decimalMultiply(decimalMultiply(decimalMultiply(perShareDelta, fillPricePerShare), CONTRACT_MULTIPLIER), contracts);
}

/** `value * 100` (exit_policy fraction -> watches 0-100 percent). */
function fractionToPercent(value: string): string {
  return decimalMultiply(value, "100");
}

/**
 * Map an exit rule to the wizard's friendly `WatchWizardInitialState` /
 * `WatchAuthoringState` string fields. Returns `null` (honest-absence, never
 * a guessed mapping) for an unrecognized `rule_kind`, or when
 * `fillPricePerShare`'s sign disagrees with what the rule_kind requires (a
 * debit-family rule_kind needs a positive fill price; `CREDIT_VERTICAL_BUYBACK`
 * needs a negative one) -- mirroring the Python mapper's
 * `ExitRuleMappingError` cases as an honest `null` here instead, since this
 * is a UI pre-fill helper, not a hard validation boundary.
 *
 * `fillPricePerShare` is the SIGNED net entry basis per share (positive =
 * net debit paid, negative = net credit received) -- see
 * `domains/watches/lib/multiLegPosition.ts`'s `derivePositionBasisPerShare`
 * for the canonical LIVE source of this value from a wizard draft, or
 * `execution_friction.net_realistic_fill_per_share` for a Desk idea's OWN
 * displayed fill (never `natural_entry_per_share`, which is unsigned).
 */
export function mapExitRuleToWatchFields(
  rule: ExitRuleLike,
  fillPricePerShare: string,
  contracts = "1",
): ExitRuleWatchFields | null {
  if (!rule.rule_kind || rule.target_value == null || rule.stop_value == null || rule.dte_floor == null) {
    return null;
  }
  const fillPrice = parseDecimal(fillPricePerShare);
  const fillIsPositive = !fillPrice.negative && fillPrice.unscaled !== 0n;
  const fillIsNegative = fillPrice.negative && fillPrice.unscaled !== 0n;

  const dteThreshold = String(rule.dte_floor);

  if (rule.rule_kind === "LONG_OPTION_VALUE_MULTIPLE") {
    if (!fillIsPositive) return null;
    return {
      profitTarget: valueMultiplePnlUsd(rule.target_value, fillPricePerShare, contracts),
      // The wizard's lossLimit field is a NON-NEGATIVE magnitude (it negates
      // internally when building the pnl_usd lte trigger -- see
      // watchPositionExitDraft.ts's buildPositionExitEvaluationSpec). The raw
      // signed threshold here is always <= 0 for a stop, so negate it back
      // to a magnitude for this friendly field.
      lossLimit: decimalNegate(valueMultiplePnlUsd(rule.stop_value, fillPricePerShare, contracts)),
      dteThreshold,
      dteOperator: "lte",
    };
  }

  if (rule.rule_kind === "DEBIT_VERTICAL_PROFIT_CAPTURE") {
    if (!fillIsPositive) return null;
    return {
      maxProfitPercent: fractionToPercent(rule.target_value),
      lossLimit: decimalNegate(valueMultiplePnlUsd(rule.stop_value, fillPricePerShare, contracts)),
      dteThreshold,
      dteOperator: "lte",
    };
  }

  if (rule.rule_kind === "CREDIT_VERTICAL_BUYBACK") {
    if (!fillIsNegative) return null;
    const creditReceived = decimalNegate(fillPricePerShare);
    // Buy-back cost reaching >= stop_value * credit received is a LOSS of
    // (stop_value - 1) * credit received -- matches the Python mapper's
    // CREDIT_VERTICAL_BUYBACK stop arithmetic exactly.
    const lossMagnitude = decimalMultiply(
      decimalMultiply(decimalSubtract(rule.stop_value, "1"), creditReceived),
      decimalMultiply(CONTRACT_MULTIPLIER, contracts),
    );
    return {
      maxProfitPercent: fractionToPercent(rule.target_value),
      lossLimit: lossMagnitude,
      dteThreshold,
      dteOperator: "lte",
    };
  }

  return null;
}
